import React, { useEffect, useState } from 'react';

const columns = [
  'BMP',
  'Land use in drainage area',
  'General shape',
  'Operating since',
  'Drainage area (acre)',
  'Design storm depth (in)',
  'Design runoff capture volume (ft³)',
  'Loading ratio',
  'Surface area (ft²)',
  'Media depth (in)',
  'Gravel layer depth (in)',
  'Ponding volume (ft³)',
  'Design ponding depth (in)',
  'Design infiltration rate (in/h)',
  'Infiltration rate (in/h)'
];

const rows = [
  ['6-1', '75% road and 25% parking lot', 'Rectangular', '2019', '0.6', '0.53', '-c', '14', '1790', '24', '15', '1790', '12', '', '29'],
  ['9-1', '65% multi-lane road and 35% park/open space', 'Rectangular', '2020', '1.3', '0.46', '1460', '160', '352', '18', '12', '176', '6', '5', '133'],
  ['10-1', '80% parking lot and 20% park/open space', 'Irregular', '2013', '1.5', '0.58', '3270', '23', '3000', '18', '0', '2500', '10', '5', '60'],
  ['12-1', '83% high-density or multi-family residential and 12% low-density residential', 'Rectangular', '2024', '2.8', '0.5', '4120', '126', '950', '18', '13', '475', '6', '', ''],
  ['12-2', '48% park/open space, 30% low-density residential, 9% road, and 9% agriculture area', 'Rectangular', '2024', '11', '0.5', '11000', '365', '1360', '18', '13', '679', '6', '', ''],
  ['8-2', 'Multi-lane road', 'Oval', '2019', '5.8', '0.85', '4790', '85', '2980', '24', '16', '40500', '163', '2.5', ''],
  ['8-3', 'Multi-lane road', 'Rectangular', '2019', '4.9', '0.85', '2180', '7', '32200', '24', '16', '286000', '108', '2.5', ''],
  ['8-4', 'Multi-lane road', 'Rectangular', '2019', '6.0', '0.85', '3050', '15', '17800', '24', '16', '142000', '96', '2.5', ''],
  ['11-1', 'Parking lot', 'Rectangular', '2018', '0.7', '0.61', '1410', '39', '804', '37', '9', '1090', '16', '', '>100'],
  ['11-2', 'Roof', 'Rectangular', '2023', '0.07', '0.61', '298', '22', '293', '18', '6', '244', '10', '8-24', '20'],
  ['11-3', 'Roof', 'Rectangular', '2023', '0.07', '0.61', '135', '14', '213', '18', '6', '177', '10', '8-24', '30'],
  ['1-1', 'Road', 'Oval', '2019', '18', '', '22,034', '35.6', '2088', '18', '12', '', '36', '1.75', ''],
  ['2-1', 'Public park', 'Triangular', '2019', '4.3', '0.52', '5080', '28', '6520', '24', '3', '8620', '18', '1.1', ''],
  ['7-1', 'Parking lot', 'Oval', '2017', '0.6', '', '782', '', '', '', '', '', '', '', '']
];

const footnotes = [
  'a. Loading ratio: contributing drainage area divided by BMP surface area.',
  'b. Ponding volume above the media surface for the design event.',
  'c. Design runoff capture volume was not available; design flow rate was 5.58 cubic feet per second.'
];

export default function BMPSiteInformationTable() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  return (
    <>
      <div className="flex justify-center items-start w-full mt-4">
        <div className="w-full max-w-4xl px-1">
          <button
            type="button"
            onClick={() => setIsOpen(true)}
            className="text-sm text-blue-700 underline underline-offset-2 hover:text-blue-900"
          >
            View site information table for monitored BMPs
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4">
          <div
            className="absolute inset-0 bg-slate-900/55"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />

          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="bmp-site-info-title"
            className="relative z-10 w-full max-w-[98vw] max-h-[90vh] overflow-hidden rounded-2xl bg-white shadow-2xl border border-gray-200"
          >
            <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-3 border-b border-gray-200">
              <div>
                <h3 id="bmp-site-info-title" className="text-lg font-semibold text-[#13273d]">
                  Table 1. Site information for monitored BMPs.
                </h3>
              </div>

              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="shrink-0 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>

            <div className="max-h-[calc(90vh-132px)] overflow-auto">
              <div className="overflow-x-auto">
                <table className="min-w-[1450px] w-full border-collapse text-sm text-left">
                  <thead className="bg-stone-100">
                    <tr>
                      {columns.map((column) => (
                        <th key={column} className="border border-gray-300 px-3 py-3 align-top font-semibold text-[#13273d]">
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr key={row[0]} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50/70'}>
                        {row.map((cell, cellIndex) => (
                          <td
                            key={`${row[0]}-${columns[cellIndex]}`}
                            className={`border border-gray-300 px-3 py-2 align-top ${cellIndex === 0 ? 'font-medium text-[#13273d]' : 'text-gray-800'}`}
                          >
                            {cell || ' '}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="px-6 py-4 bg-stone-50 border-t border-gray-200">
                {footnotes.map((note) => (
                  <p key={note} className="text-xs leading-relaxed text-gray-700">
                    {note}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
